<template>
  <div class="pos-id-pagination d-flex flex-wrap align-items-center justify-content-between">
    <div class="pos-id-pagination__meta d-flex flex-wrap align-items-center">
      <span class="pos-id-pagination__total">共 {{ total }} {{ unit }}</span>
      <span class="pos-id-pagination__divider">|</span>
      <span class="pos-id-pagination__label">每頁</span>
      <b-form-select
        :value="safeCount"
        :options="countOptions"
        size="sm"
        class="pos-id-pagination__size"
        @change="onCountChange"
      />
      <span class="pos-id-pagination__label">筆</span>
    </div>

    <div class="pos-id-pagination__controls d-flex flex-wrap align-items-center">
      <b-pagination
        :value="safePage"
        :total-rows="total"
        :per-page="safeCount"
        first-number
        last-number
        class="pos-id-pagination__pages mb-0"
        prev-class="prev-item"
        next-class="next-item"
        @change="onPageChange"
      >
        <template #prev-text>
          <feather-icon icon="ChevronLeftIcon" size="18" />
        </template>
        <template #next-text>
          <feather-icon icon="ChevronRightIcon" size="18" />
        </template>
      </b-pagination>
      <span class="pos-id-pagination__page-text">第 {{ safePage }} / {{ totalPages }} 頁</span>
      <span class="pos-id-pagination__label pos-id-pagination__goto-label">前往</span>
      <b-form-input
        v-model="pageInput"
        type="number"
        size="sm"
        min="1"
        :max="totalPages"
        class="pos-id-pagination__jump"
        @keyup.enter="jumpToPage"
      />
      <span class="pos-id-pagination__label">頁</span>
      <b-button
        size="sm"
        variant="outline-secondary"
        class="pos-id-pagination__jump-btn"
        @click="jumpToPage"
      >
        跳轉
      </b-button>
    </div>
  </div>
</template>

<script>
import { Message } from 'element-ui'

const DEFAULT_COUNT_OPTIONS = [10, 20, 50, 100, 200]

/**
 * pos-id 四頁共用的表格底部分頁條：
 * 左：共 N 筆 + 每頁筆數；右：頁碼 + 第 x / y 頁 + 前往指定頁。
 * 翻頁 / 改每頁筆數 / 跳頁都用事件交給父層處理（父層負責 page=1 與重新查詢）。
 */
export default {
  name: 'PosIdPagination',
  props: {
    total: {
      type: Number,
      default: 0
    },
    page: {
      type: Number,
      default: 1
    },
    count: {
      type: Number,
      default: 20
    },
    unit: {
      type: String,
      default: '筆'
    },
    countOptions: {
      type: Array,
      default: () => DEFAULT_COUNT_OPTIONS
    }
  },
  data() {
    return {
      pageInput: '1'
    }
  },
  computed: {
    safeCount() {
      const value = Math.floor(Number(this.count))
      return value > 0 ? value : 20
    },
    totalPages() {
      return Math.max(1, Math.ceil(Number(this.total || 0) / this.safeCount))
    },
    safePage() {
      const value = Math.floor(Number(this.page))
      if (!(value > 0)) {
        return 1
      }
      return Math.min(value, this.totalPages)
    }
  },
  watch: {
    safePage: {
      handler(value) {
        this.pageInput = String(value)
      },
      immediate: true
    }
  },
  methods: {
    onPageChange(page) {
      const target = Math.floor(Number(page))
      if (!(target > 0) || target === this.safePage) {
        return
      }
      this.$emit('page-change', Math.min(target, this.totalPages))
    },
    onCountChange(count) {
      const target = Math.floor(Number(count))
      if (!(target > 0) || target === this.safeCount) {
        return
      }
      this.$emit('count-change', target)
    },
    jumpToPage() {
      const raw = this.pageInput === undefined || this.pageInput === null ? '' : String(this.pageInput).trim()
      if (!/^[1-9][0-9]*$/.test(raw)) {
        Message.warning(`請輸入 1 至 ${this.totalPages} 之間的頁數`)
        this.pageInput = String(this.safePage)
        return
      }
      const target = Math.min(Number(raw), this.totalPages)
      this.pageInput = String(target)
      if (target === this.safePage) {
        return
      }
      this.$emit('page-change', target)
    }
  }
}
</script>

<style lang="scss" scoped>
.pos-id-pagination {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #edf1f6;
  color: #5b6675;
  font-size: 12.5px;

  &__total {
    color: #253041;
    font-weight: 600;
  }

  &__divider {
    margin: 0 10px;
    color: #d3dae4;
  }

  &__label {
    margin: 0 6px;
    white-space: nowrap;
  }

  &__size {
    width: 84px;
  }

  &__page-text {
    margin-left: 14px;
    white-space: nowrap;
  }

  &__goto-label {
    margin-left: 14px;
  }

  &__jump {
    width: 64px;
  }

  &__jump-btn {
    margin-left: 6px;
  }
}
</style>
